import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';

import type { AccessSchema } from '@models/access-management';

import type {
  RemoveAllUserAccessMutation,
  RemoveAllUserAccessMutationVariables,
  UpsertUserAccessManagementMutation,
  UpsertUserAccessManagementMutationVariables,
} from 'generated/access-management.generated';
import {
  ListUserAccessManagementDocument, // ListUserAccessManagementDocument,
  useListUserAccessManagementQuery,
  useRemoveAllUserAccessMutation,
  useUpsertUserAccessManagementMutation,
} from 'generated/access-management.generated';
import {
  PermissionControlScope,
  type ListPermissionControlSetsInput,
} from 'generated/types';

export const useGetUserAccessManagement = (
  params: ListPermissionControlSetsInput
) => {
  const { handleError } = useResponseHandler();
  const response = useListUserAccessManagementQuery({
    variables: { input: { ...params } },
    skip: !params.accessing_user_id || !params.user_id,
    // fetchPolicy: 'no-cache',
  });
  handleError(response.error);
  const data = (response.data?.listUserPermissionControlSets ?? []).reduce<
    AccessSchema[]
  >((acc, { controls, name, permission_control_set_id }) => {
    acc.push({
      control_set_id: permission_control_set_id,
      name,
      grants: controls.map(
        ({ name: grantName, nodes, permission_control_id }) => {
          return {
            grantName,
            permission_control_id,
            view: nodes.find(
              ({ scope }) => scope === PermissionControlScope.read
            ),
            edit: nodes.find(
              ({ scope }) => scope === PermissionControlScope.write
            ),
          };
        }
      ),
    });

    return acc;
  }, []);

  const hasNoAccess = data
    .reduce<boolean[]>((acc, { grants }) => {
      grants.forEach((node) => {
        if (typeof node.edit?.is_enabled === 'boolean') {
          acc.push(node.edit.is_enabled);
        }
        if (typeof node.view?.is_enabled === 'boolean') {
          acc.push(node.view.is_enabled);
        }
      });
      return acc;
    }, [])
    .every((value) => !value);

  return { ...response, data, hasNoAccess };
};

export function useUpsertUserAccessManagement(): [
  (
    params: UpsertUserAccessManagementMutationVariables
  ) => Promise<FetchResult<UpsertUserAccessManagementMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'upsertAccessMgmt',
  });
  const [mutate, { loading: baseLoading }] =
    useUpsertUserAccessManagementMutation();
  const loading = baseLoading;

  async function updateUserAccessManagement(
    params: UpsertUserAccessManagementMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (response?.postUserPermissionGrants) {
          /**
           * Evicts the cache for the mutated user.
           *
           * This is done to ensure that the cache reflects the latest
           * permission grants after a mutation.
           */
          cache.evict({
            // Identify the user's cache entry
            id: cache.identify({
              __typename: 'UserModel',
              user_id: params.input.accessing_user_id,
            }),
            // Evict their access_level field for the owner user
            fieldName: 'access_level',
            // Pass the user_id for which the grants were mutated
            args: {
              user_id: params.input.user_id,
            },
          });
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.postUserPermissionGrants && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateUserAccessManagement, loading];
}

export function useRemoveAllUserAccessSettings(): [
  (
    params: RemoveAllUserAccessMutationVariables
  ) => Promise<FetchResult<RemoveAllUserAccessMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'deletePermissionGrants',
  });
  const [mutate, { loading: baseLoading }] = useRemoveAllUserAccessMutation({
    awaitRefetchQueries: true,
  });
  const loading = baseLoading;

  async function removeAllAccess(params: RemoveAllUserAccessMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      refetchQueries: [
        {
          query: ListUserAccessManagementDocument,
          variables: {
            input: {
              accessing_user_id: params.accessing_user_id,
              user_id: params.owner_user_id,
            },
          },
        },
      ],
      update: (cache, { data: response }) => {
        // Only perform the cache eviction if the mutation was successful
        if (response?.deleteUserPermissionGrants) {
          /**
           * Evicts the cache for the mutated user.
           *
           * This is done to ensure that the cache reflects the latest
           * permission grants after a mutation.
           */
          cache.evict({
            // Identify the user's cache entry
            id: cache.identify({
              __typename: 'UserModel',
              user_id: params.accessing_user_id,
            }),
            // Evict their access_level field for the owner user
            fieldName: 'access_level',
            // Pass the user_id for which the grants were mutated
            args: {
              user_id: params.owner_user_id,
            },
          });
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.deleteUserPermissionGrants && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [removeAllAccess, loading];
}
